import { ActionReducerMapBuilder, createSlice, PayloadAction } from '@reduxjs/toolkit';

import { UserType } from 'api';
import { NotificationType } from 'common';
import { readStoredTokens } from 'config';
import { LsKeys } from 'enums';
import {
  changePassword,
  getMe,
  login,
  restorePassword,
  verifyReset,
} from 'state/thunks/authThunk/authThunk';

const initialState = {
  initialized: false as boolean,
  accessToken: '' as string,
  error: '' as string,
  // `Loader` is rendered unconditionally in App.tsx and paints a full-screen
  // overlay (z-index 25) whenever this is true, so it must mean "a session is
  // being restored right now" -- not "the app just started". Hardcoded `true`,
  // it was only ever cleared as a side effect of PublicLayout calling getMe()
  // on every visit, including logged-out ones. Once that call was correctly
  // made conditional, the overlay stayed up forever on /login and swallowed
  // every click on the form.
  isLoading: !!readStoredTokens().accessToken,
  userInfo: {} as UserType,
  language: 'English',
  resetToken: '' as string,
  // True only between a confirmed reset code and the password actually being
  // changed. /change-password is a public route, so this flag is what stops
  // the form being reachable by typing the URL -- see the guard in the page.
  resetVerified: false as boolean,
  // The number the current reset was started for, so "Resend code" works.
  resetPhone: '' as string,
  // Both absolute ISO instants from the server, not durations.
  //
  // The code screen renders a countdown from them, and it has to be the
  // SERVER's deadline rather than one started when the response was parsed. A
  // locally started timer drifts, pauses when the tab is backgrounded, and
  // restarts on a remount -- and every one of those offers a Resend button that
  // the server then answers with OTP_RESEND_TOO_SOON.
  resetExpiresAt: '' as string,
  resetResendAvailableAt: '' as string,
};

const slice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    // Not a bare `() => initialState`: that would restore whatever
    // `isLoading` was computed at module load, which is `true` for anyone who
    // had a session when the page loaded -- leaving the overlay up over the
    // login form they were just sent to.
    setLogOut: () => ({ ...initialState, isLoading: false }),
    clearError: state => {
      state.error = '';
    },
    // Lets a page report a client-side problem through the same slot the API
    // errors use, so there is exactly one place a form shows what went wrong.
    setError: (state, action: PayloadAction<string>) => {
      state.error = action.payload;
    },
  },
  extraReducers: (builder: ActionReducerMapBuilder<any>) => {
    builder
      .addCase(login.fulfilled, (state, action: PayloadAction<any>) => {
        state.accessToken = action.payload.accessToken;
        state.userInfo = action.payload.user;
        state.verified = true;

        const data = {
          accessToken: action.payload.accessToken,
          refreshToken: action.payload.refreshToken,
        };

        localStorage.setItem(LsKeys.TOKEN_INFO, JSON.stringify(data));
        state.isLoading = false;
      })
      .addCase(login.pending, (state, action: PayloadAction<any>) => {
        state.isLoading = true;
      })
      .addCase(login.rejected, (state, action: PayloadAction<any>) => {
        state.error = action.payload;
        state.isLoading = false;
      })
      .addCase(getMe.pending, (state, action: PayloadAction<any>) => {
        state.isLoading = true;
        state.verified = true;
      })
      .addCase(getMe.fulfilled, (state, action: PayloadAction<any>) => {
        // A-04: the private routes only ever checked that *a* token existed,
        // never whose. A patient's or doctor's token sitting in localStorage
        // rendered the whole console -- sidebar, dashboard, charts -- while
        // every data call came back 403 and displayed nothing. Verified with a
        // real patient token: /dashboard rendered and six requests 403'd.
        //
        // /auth/sign-in-admin already refuses non-admin credentials (401), so
        // this only catches a token that arrived some other way: a shared
        // browser, a stale key, or one pasted in by hand. Rejecting here
        // covers all ten private routes at once, because they all wait on
        // getMe before rendering.
        if (action.payload?.role !== 'Admin') {
          localStorage.removeItem(LsKeys.TOKEN_INFO);
          state.accessToken = '';
          state.userInfo = {} as UserType;
          state.error = 'This account is not an administrator.';
          state.initialized = true;
          state.isLoading = false;

          return;
        }

        state.userInfo = {
          ...state.userInfo,
          ...action.payload,
          isLoading: false,
        };
        // Was `state.accessToken = true` — a boolean in a field typed as the
        // token string. It happened to satisfy the truthiness checks that gate
        // the private routes, but it overwrote the real token on every session
        // restore. Read the actual value back from storage instead.
        state.accessToken = readStoredTokens().accessToken ?? state.accessToken;
        state.initialized = true;
        state.isLoading = false;
      })
      .addCase(getMe.rejected, (state, action: PayloadAction<any>) => {
        // The session could not be restored: drop the stale token so the
        // private layout redirects to /login instead of rendering a shell that
        // 401s on every request.
        state.accessToken = '';
        state.userInfo = {} as UserType;
        state.initialized = true;
        state.isLoading = false;
      })
      .addCase(restorePassword.fulfilled, (state, action: any) => {
        // The `alert(action.payload.code)` that used to fire here was a debug
        // leftover. The server stopped returning `code` (it is delivered by
        // SMS only), so by the end it popped a modal reading "undefined" on
        // every reset attempt -- and had it still been returned, this would
        // have put the one-time code on screen for anyone looking.
        //
        // `.payload.token`, not `.payload`. While the OTP was removed this
        // endpoint answered with the token as its whole payload; it returns an
        // object now, and assigning that object to a string field left
        // `resetToken` holding "[object Object]" -- truthy, so every guard
        // passed, and the request that finally used it failed on a malformed
        // token with nothing pointing at the cause.
        const issued = action.payload?.payload ?? {};

        state.resetToken = issued.token ?? '';
        // NOT verified. Step 1 proves nothing, and that is the entire point of
        // restoring the OTP: a phone number alone no longer authorises a reset.
        // This flag gates /change-password, so granting it here -- as it was
        // while the code screen was skipped -- would let anyone who knows an
        // admin's number walk straight to the new-password form.
        state.resetVerified = false;
        // The server's normalised form in preference to what was typed: the
        // challenge is keyed on it, so a resend echoing the raw input can miss.
        state.resetPhone =
          issued.phone ?? action.meta?.arg?.restoreData?.phone ?? '';
        state.resetExpiresAt = issued.expiresAt ?? '';
        state.resetResendAvailableAt = issued.resendAvailableAt ?? '';
        state.isLoading = false;
      })
      .addCase(restorePassword.pending, state => {
        state.isLoading = true;
      })
      .addCase(restorePassword.rejected, (state, action: PayloadAction<any>) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // verifyReset and changePassword had no cases at all: a failure left
      // `error` untouched, so the page rendered its (empty) error slot and the
      // admin saw a form that simply did nothing when submitted.
      .addCase(verifyReset.pending, state => {
        state.isLoading = true;
      })
      .addCase(verifyReset.fulfilled, (state, action: any) => {
        // The token is EXCHANGED here, not merely blessed.
        //
        // Step 2 hands back a DIFFERENT token, with a different purpose
        // (`reset_commit`) and a fresh nonce, and step 3's guard demands that
        // purpose. Keeping the step-1 token would fail the final request with a
        // 403 that reads like an expired session. Replacing it is also what
        // keeps the flow single-use: the step-1 nonce is burned the moment this
        // succeeds, so the token being dropped here is already spent.
        state.resetToken = action.payload?.payload?.token ?? state.resetToken;
        state.resetVerified = true;
        state.isLoading = false;
      })
      .addCase(verifyReset.rejected, (state, action: PayloadAction<any>) => {
        state.resetVerified = false;
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(changePassword.pending, state => {
        state.isLoading = true;
      })
      .addCase(changePassword.fulfilled, state => {
        // One-shot: the token and the confirmation are spent, so returning to
        // /change-password requires starting the flow again.
        state.resetToken = '';
        state.resetVerified = false;
        state.resetPhone = '';
        state.resetExpiresAt = '';
        state.resetResendAvailableAt = '';
        state.isLoading = false;
      })
      .addCase(changePassword.rejected, (state, action: PayloadAction<any>) => {
        state.isLoading = false;
        state.error = action.payload;
      });
  },
});

export const authActions = slice.actions;

export const authReducer = slice.reducer;
